import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

import { getUserById } from "../../api/User/userApi";
import { jwtDecode } from "jwt-decode";

import { useTranslation } from "react-i18next";

import {
  Box,
  Badge,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Popover,
  Select,
  MenuItem,
  Stack,
  TextField,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  getAllMuestras,
  getMuestrasEstado,
  getTallas,
  getColoresMuestras,
  getProveedores,
  getVistaMuestrasDesigners,
  getMuestraById,
  getEscandallosByMuestraId,
  getEscandalloGrupos,
  getMarcas,
  getMuestrasTipo,
  getEscandalloSubgruposMuestras,
  getMuestrasTemporadas,
  getMovimientosDetallados,
} from "../../api/Muestras/muestraApi";
import { getMuestraSupplierResponsables } from "../../api/Muestras/muestraSupplierResponsablesApi";
import { canUserAccessProductTree } from "../../utils/userPermissionsUtils";

import DrawerMuestraV2 from "./Drawer/DrawerMuestraV2";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import MuestraArchivosCell from "./MuestraArchivosCell";
import MuestraCategoryFilesCell from "./MuestraCategoryFilesCell";
import MuestrasExportExcelDialog from "./MuestrasExportExcelDialog";
import MuestrasExportPdfDialog from "./MuestrasExportPdfDialog";
import MuestrasPresentacionCamara from "./MuestrasPresentacionCamara";
import DesignTechnicalFilesDialog from "./DesignTechnicalFilesDialog";
import NotasBadge from "../CriticalPathHorizontal/NotasBadge";
import MuestraChat from "./MuestraChat";
import { getEntityChatSummary } from "../../api/Chat/entityChatApi";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { setBrowserTabBadge } from "../../utils/browserTabBadge";
import EntityAvatar from "../CriticalPathHorizontal/EntityAvatar";
import {
  getClientColor,
  getSupplierColor,
  getDesignerColor,
  getDevelopmentColor,
} from "../CriticalPathHorizontal/criticalPathAvatars";
import {
  getNotasCountAll,
  getNotasByEntityWithUser,
  getNotasNuevas,
} from "../../api/CriticalPath/criticalPathNotasApi";
import { getCpNotasEntityKey } from "../../utils/cpNotasEntityKey";
import { socket } from "../../socket";
import { getProducts } from "../../api/Design/productApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import {
  pageMobileCardSx,
  pageFieldSx,
  pageContentCardSx,
  pageDataGridSx,
} from "../../styles/pageModernStyles";
import DryCleaningOutlinedIcon from "@mui/icons-material/DryCleaningOutlined";

const buildMovimientosResumenByMuestra = (movimientos = []) => {
  const resumen = {};

  for (const mov of movimientos) {
    const id = mov.ID_MUESTRA;
    if (!id) continue;

    if (!resumen[id]) {
      resumen[id] = {
        count: 0,
        ultimoDestino: null,
        ultimoFecha: null,
        ultimoFechaTs: 0,
        couriers: [],
      };
    }

    resumen[id].count += 1;

    const courier = String(mov.NUM_COURIER || "").trim();
    if (courier && !resumen[id].couriers.includes(courier)) {
      resumen[id].couriers.push(courier);
    }

    if (!mov.FECHA_ENVIO) continue;

    const fechaTs = new Date(mov.FECHA_ENVIO).getTime();
    if (!Number.isFinite(fechaTs)) continue;

    if (fechaTs >= resumen[id].ultimoFechaTs) {
      resumen[id].ultimoFechaTs = fechaTs;
      resumen[id].ultimoFecha = mov.FECHA_ENVIO;
      // Prefer new typed destination; fall back to PLACED/MARCA (legacy).
      const destino = mov.DESTINO_NOMBRE || mov.PLACED || mov.MARCA || null;
      const persona = String(mov.PERSONA_ENVIO || "").trim();
      resumen[id].ultimoDestino =
        destino && persona ? `${destino} · ${persona}` : destino;
    }
  }

  return resumen;
};

const enrichMuestraMovimientos = (muestra, resumen = {}) => {
  const info = resumen[muestra.ID_MUESTRA || muestra.id];

  return {
    ...muestra,
    MOVIMIENTOS_COUNT: info?.count || 0,
    ULTIMO_ENVIO_DESTINO: info?.ultimoDestino || null,
    ULTIMO_ENVIO_FECHA: info?.ultimoFecha ? new Date(info.ultimoFecha) : null,
    COURIERS: info?.couriers || [],
  };
};

const applyMovimientosResumen = (muestras, movimientos) => {
  const resumen = buildMovimientosResumenByMuestra(movimientos);
  return muestras.map((muestra) => enrichMuestraMovimientos(muestra, resumen));
};

const Muestras = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const { t } = useTranslation();

  const [muestras, setMuestras] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tallas, setTallas] = useState([]);
  const [colores, setColores] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [escandalloGrupos, setEscandalloGrupos] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [cameraPresentationOpen, setCameraPresentationOpen] = useState(false);
  const [selectedMuestra, setSelectedMuestra] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDesigners, setSelectedDesigners] = useState([]);
  const [selectedEstado, setSelectedEstado] = useState([]);
  const [selectedTallas, setSelectedTallas] = useState([]);
  const [selectedColores, setSelectedColores] = useState([]);
  const [clienteFilter, setClienteFilter] = useState("");
  const [proveedorFilter, setProveedorFilter] = useState("ALL");
  const [selectedMarcas, setSelectedMarcas] = useState([]);
  const [selectedResponsables, setSelectedResponsables] = useState([]);
  const [tiposResponsables, setTiposResponsables] = useState([]);
  const [selectedPrioridades, setSelectedPrioridades] = useState([]);
  const [selectedDevelopments, setSelectedDevelopments] = useState([]);
  const [selectedCalidadEstados, setSelectedCalidadEstados] = useState([]);
  const [selectedPrintEstados, setSelectedPrintEstados] = useState([]);
  const [hideArrived, setHideArrived] = useState(true);
  const [hideSentToClient, setHideSentToClient] = useState(true);
  const [showUnreadChats, setShowUnreadChats] = useState(false);
  const [products, setProducts] = useState([]);
  const [tiposMuestra, setTiposMuestra] = useState([]);
  const [selectedTemporadas, setSelectedTemporadas] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  const [notasMetaMap, setNotasMetaMap] = useState({});
  const [notasUnreadMap, setNotasUnreadMap] = useState({});
  const [temporadaColorMap, setTemporadaColorMap] = useState({});
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMuestra, setChatMuestra] = useState(null);
  const [chatMetaMap, setChatMetaMap] = useState({});
  const [chatInitialTab, setChatInitialTab] = useState("mensajes");
  const [designFilesDialog, setDesignFilesDialog] = useState({
    open: false,
    productId: null,
    muestraId: null,
    muestraName: "",
    clientName: "",
    designLabel: "",
  });

  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [selectedMuestraIds, setSelectedMuestraIds] = useState([]);

  // China
  const [userData, setUserData] = useState(null);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  // AÑADIR MUESTRAS AL CRITICAL PATH
  const userCanCreateSample = decoded?.PermisoCreateSample === true;
  const canAccessProductTree = canUserAccessProductTree(userData);

  //
  // CARGA INICIAL: primero usuario -> luego fetchData(user)
  //
  useEffect(() => {
    const load = async () => {
      if (!token) return navigate("/login");

      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;
        if (decoded.exp < now) {
          localStorage.removeItem("token");
          return navigate("/login");
        }

        // 1. Cargar usuario
        const user = await getUserById(decoded.userId);
        setUserData(user);

        // 2. Cargar muestras ya con el usuario disponible
        await fetchData(user);
      } catch (e) {
        console.error("Error loading user:", e);
        navigate("/login");
      }
    };

    load();
  }, [navigate]);

  const loadNotasCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const data = await getNotasCountAll();
      const metaMap = {};
      data.forEach((row) => {
        if (row?.ID_MUESTRA == null) return;
        const key = `M_${Number(row.ID_MUESTRA)}`;
        metaMap[key] = {
          total: Number(row.total) || 0,
          openIssueCount: Number(row.openIssueCount) || 0,
        };
      });
      setNotasMetaMap(metaMap);

      if (token) {
        const { userId } = jwtDecode(token);
        const unreadData = await getNotasNuevas(userId);
        const unreadMap = {};
        (unreadData.porProducto || []).forEach((row) => {
          const entityKey =
            row.EntityKey ||
            (row.ID_MUESTRA != null ? `M_${Number(row.ID_MUESTRA)}` : null);
          if (!entityKey || !String(entityKey).startsWith("M_")) return;
          unreadMap[entityKey] = Number(row.count) || 0;
        });
        setNotasUnreadMap(unreadMap);
      }
    } catch (error) {
      console.error("Error loading muestra notas counts:", error);
    }
  }, []);

  const loadChatSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { userId } = jwtDecode(token);
      const summary = await getEntityChatSummary("Muestra", userId);
      const normalized = {};
      Object.entries(summary || {}).forEach(([id, meta]) => {
        normalized[Number(id)] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
        };
      });
      setChatMetaMap(normalized);
    } catch (error) {
      console.error("Error loading muestra chat summary:", error);
    }
  }, []);

  useEffect(() => {
    getTemporadas()
      .then((rows) => {
        const map = {};
        (rows || []).forEach((t) => {
          if (t?.TEMPORADA && t?.COLOR_HEX) {
            map[t.TEMPORADA] = t.COLOR_HEX;
          }
        });
        setTemporadaColorMap(map);
      })
      .catch((error) => {
        console.error("Error loading temporadas catalog:", error);
      });
  }, []);

  useEffect(() => {
    loadNotasCounts();
    loadChatSummary();
    const handleNotasUpdated = () => loadNotasCounts();
    const currentUserId = decoded?.userId;
    const handleChatNuevo = ({
      entityType,
      entityId,
      mentionedUserIds,
      mensaje,
    }) => {
      if (entityType !== "Muestra") return;
      const id = Number(entityId);
      const openHere =
        chatOpen && Number(chatMuestra?.ID_MUESTRA || chatMuestra?.id) === id;
      const isMentioned =
        Number(mensaje?.AutorId) !== Number(currentUserId) &&
        (mentionedUserIds || []).map(Number).includes(Number(currentUserId));

      setChatMetaMap((prev) => {
        const cur = prev[id] || { total: 0, noLeidos: 0, hasMention: false };
        return {
          ...prev,
          [id]: {
            total: (cur.total || 0) + 1,
            noLeidos: openHere ? 0 : (cur.noLeidos || 0) + 1,
            hasMessages: true,
            hasMention: openHere
              ? false
              : Boolean(cur.hasMention) || isMentioned,
          },
        };
      });

      if (isMentioned && !openHere) {
        setBrowserTabBadge(true);
      }
    };
    const handleChatLeidos = ({ entityType, entityId, userId: readBy }) => {
      if (entityType !== "Muestra") return;
      if (
        readBy != null &&
        currentUserId != null &&
        Number(readBy) !== Number(currentUserId)
      ) {
        return;
      }
      const id = Number(entityId);
      setChatMetaMap((prev) => {
        const cur = prev[id];
        if (!cur) return prev;
        return {
          ...prev,
          [id]: { ...cur, noLeidos: 0, hasMention: false },
        };
      });
      setBrowserTabBadge(false);
    };
    const handleVisibility = () => {
      if (!document.hidden) {
        setChatMetaMap((prev) => {
          const anyMention = Object.values(prev).some((m) => m?.hasMention);
          if (!anyMention) setBrowserTabBadge(false);
          return prev;
        });
      }
    };
    socket.on("criticalpath-nota-count-updated", handleNotasUpdated);
    socket.on("chat-nuevo-mensaje", handleChatNuevo);
    socket.on("chat-mensajes-leidos", handleChatLeidos);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      socket.off("criticalpath-nota-count-updated", handleNotasUpdated);
      socket.off("chat-nuevo-mensaje", handleChatNuevo);
      socket.off("chat-mensajes-leidos", handleChatLeidos);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [
    loadNotasCounts,
    loadChatSummary,
    chatOpen,
    chatMuestra,
    decoded?.userId,
  ]);

  const refreshMuestraNotasMeta = useCallback(
    async (idMuestra) => {
      if (idMuestra == null) return;
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { userId } = jwtDecode(token);
        const notas = await getNotasByEntityWithUser({
          productId: null,
          idMuestra,
          idPresupuestoA3Pedido: null,
          userId,
        });
        const key = getCpNotasEntityKey({ idMuestra });
        if (!key) return;
        const openIssueCount = (notas || []).filter(
          (n) =>
            n.TipoNota === "Open Issue" &&
            (n.Status == null || n.Status !== "Closed"),
        ).length;
        setNotasMetaMap((prev) => ({
          ...prev,
          [key]: {
            total: notas.length,
            openIssueCount,
          },
        }));
        setNotasUnreadMap((prev) => ({
          ...prev,
          [key]: (notas || []).filter((n) => !n.isRead).length,
        }));
      } catch (error) {
        console.error("Error refreshing muestra notas count:", error);
        loadNotasCounts();
      }
    },
    [loadNotasCounts],
  );

  const openMuestraChat = useCallback((muestra, tab = "mensajes") => {
    const id = Number(muestra?.ID_MUESTRA || muestra?.id);
    if (!Number.isFinite(id)) return;
    setChatInitialTab(tab);
    setChatMuestra(muestra || { ID_MUESTRA: id });
    setChatOpen(true);
    setChatMetaMap((prev) => {
      const cur = prev[id] || { total: 0, noLeidos: 0 };
      const next = {
        ...prev,
        [id]: {
          ...cur,
          noLeidos: 0,
          hasMention: false,
          hasMessages: (cur.total || 0) > 0 || cur.hasMessages,
        },
      };
      const anyMention = Object.values(next).some((m) => m?.hasMention);
      if (!anyMention) setBrowserTabBadge(false);
      return next;
    });
  }, []);

  const handleOpenNotas = ({ idMuestra }) => {
    const id = Number(idMuestra);
    if (!Number.isFinite(id)) return;
    const found = (muestras || []).find(
      (m) => Number(m.ID_MUESTRA || m.id) === id,
    ) || { ID_MUESTRA: id };
    openMuestraChat(found, "notas");
  };

  const handleOpenChat = (muestra, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    openMuestraChat(muestra, "mensajes");
  };

  const getMuestraChatMeta = (muestra) => {
    const id = Number(muestra?.ID_MUESTRA || muestra?.id);
    const meta = chatMetaMap[id] || {};
    return {
      total: Number(meta.total) || 0,
      noLeidos: Number(meta.noLeidos) || 0,
      hasMention: Boolean(meta.hasMention),
      hasMessages: Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0,
    };
  };

  const renderChatDot = (muestra) => {
    const { noLeidos, hasMessages, hasMention } = getMuestraChatMeta(muestra);
    return (
      <Tooltip
        title={
          hasMention
            ? t("Samples.chatMention", { defaultValue: "You were mentioned" })
            : noLeidos > 0
              ? `${noLeidos} unread`
              : hasMessages
                ? "Chat"
                : t("Samples.chat", { defaultValue: "Chat" })
        }
      >
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => handleOpenChat(muestra, e)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "0.7rem",
            fontWeight: 800,
            color: "#fff",
            bgcolor: hasMention
              ? "warning.main"
              : noLeidos > 0
                ? "error.main"
                : hasMessages
                  ? "info.main"
                  : alpha(theme.palette.text.secondary, 0.35),
            boxShadow: hasMention ? "0 0 0 2px rgba(237,108,2,0.35)" : "none",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {noLeidos > 0 ? (
            noLeidos
          ) : (
            <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
          )}
        </Box>
      </Tooltip>
    );
  };

  const handleOpenDesignFiles = (muestra, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!muestra?.ID_PRODUCTO) return;
    setDesignFilesDialog({
      open: true,
      productId: muestra.ID_PRODUCTO,
      muestraId: muestra.ID_MUESTRA || null,
      muestraName: muestra.MUESTRA || "",
      clientName: muestra.NombreCliente || muestra.NOMBRE_DISENO || "",
      designLabel: muestra.NOMBRE_DISENO || "",
    });
  };

  const handleCloseDesignFiles = () => {
    setDesignFilesDialog({
      open: false,
      productId: null,
      muestraId: null,
      muestraName: "",
      clientName: "",
      designLabel: "",
    });
  };

  const getMuestraNotasKey = (muestra) => {
    const id = muestra?.ID_MUESTRA ?? muestra?.id;
    if (id == null) return null;
    return `M_${Number(id)}`;
  };

  const getMuestraNotasMeta = (muestra) => {
    const key = getMuestraNotasKey(muestra);
    if (!key) return { total: 0, openIssueCount: 0, unreadCount: 0 };
    const meta = notasMetaMap[key] || { total: 0, openIssueCount: 0 };
    return {
      total: meta.total || 0,
      openIssueCount: meta.openIssueCount || 0,
      unreadCount: notasUnreadMap[key] || 0,
    };
  };

  const fetchData = async (user) => {
    try {
      // 🔹 1. CARGA CRÍTICA (lo mínimo para pintar la tabla)
      const [
        muestrasData,
        estadosData,
        tallasData,
        coloresData,
        proveedoresData,
        designersData,
        marcasData,
        temporadasDataRaw,
      ] = await Promise.all([
        getAllMuestras(),
        getMuestrasEstado(),
        getTallas(),
        getColoresMuestras(),
        getProveedores(),
        getVistaMuestrasDesigners(),
        getMarcas(),
        getMuestrasTemporadas(),
      ]);

      let responsablesData = [];
      try {
        responsablesData = await getMuestraSupplierResponsables();
      } catch (error) {
        console.error("Error loading supplier responsables catalog:", error);
      }

      /* ========= MAPS ========= */
      const estadosMap = Object.fromEntries(
        estadosData.map((e) => [e.ID_ESTADO, e]),
      );
      const tallasMap = Object.fromEntries(
        tallasData.map((t) => [t.ID_TALLA, t]),
      );
      const coloresMap = Object.fromEntries(
        coloresData.map((c) => [c.ID_COLOR, c]),
      );
      const proveedoresMap = Object.fromEntries(
        proveedoresData.map((p) => [String(p.CODPROV), p]),
      );
      const designersMap = Object.fromEntries(
        designersData.map((d) => [d.ID_MUESTRA, d]),
      );
      const marcasMap = Object.fromEntries(
        marcasData.map((m) => [String(m.ID_MARCA), m]),
      );
      const temporadasMap = Object.fromEntries(
        temporadasDataRaw.map((t) => [t.ID_MUESTRA, t]),
      );
      const responsablesMap = Object.fromEntries(
        (responsablesData || []).map((r) => [r.ID, r]),
      );

      /* ========= CONSTRUIR MUESTRAS ========= */
      const muestrasConDatos = muestrasData.map((muestra) => {
        const estado = estadosMap[muestra.ID_ESTADO];
        const talla = tallasMap[muestra.ID_TALLA_BASE];
        const color = coloresMap[muestra.ID_COLOR_BASE];
        const proveedor = proveedoresMap[String(muestra.PROVEEDOR)];
        const designer = designersMap[muestra.ID_MUESTRA];
        const marca = marcasMap[String(muestra.ID_Marca)];
        const temporada = temporadasMap[muestra.ID_MUESTRA];
        const responsable =
          muestra.responsableSupplier ||
          responsablesMap[muestra.ID_RESPONSABLE_SUPPLIER];

        return {
          ...muestra,
          ESTADO_NOMBRE: estado?.ESTADO || "Desconocido",
          TALLA_NOMBRE: talla?.TALLA || "Desconocida",
          COLOR_PANTONE: color?.PANTONE || "Desconocido",
          COLOR_HEX: color?.HEX || "Desconocido",
          NOMBRE_PROVEEDOR: proveedor?.N_PROVEEDOR || "Desconocido",
          RESPONSABLE_SUPPLIER: responsable?.Nombre || "",
          DISENADOR: designer?.username || "Desconocido",
          MARCA: marca?.MARCA || "Sin Marca",
          NOMBRE_DISENO: muestra.NombreCliente || "",
          TEMPORADA: temporada?.TEMPORADA || "NO SEASON",
          FECHA_PREVISTA: muestra.FECHA_PREVISTA
            ? new Date(muestra.FECHA_PREVISTA)
            : null,
          FECHA_GET_SUPPLIER: muestra.FECHA_GET_SUPPLIER
            ? new Date(muestra.FECHA_GET_SUPPLIER)
            : null,
          FECHA_PREVISTA_FABRICA: muestra.FECHA_PREVISTA_FABRICA
            ? new Date(muestra.FECHA_PREVISTA_FABRICA)
            : null,
          id: muestra.ID_MUESTRA,
        };
      });

      /* ========= FILTRO CHINA ========= */
      let filtradas = muestrasConDatos;
      if (user?.ProveedoresPermitidos) {
        try {
          const allowed = JSON.parse(user.ProveedoresPermitidos);
          const list =
            Array.isArray(allowed) && allowed.length > 0 ? allowed : null;
          if (list) {
            filtradas = muestrasConDatos.filter((m) =>
              list.includes(m.NOMBRE_PROVEEDOR),
            );
          }
        } catch (_) {
          // ProveedoresPermitidos inválido: no filtrar
        }
      }

      /* ========= ORDEN ========= */
      filtradas.sort((a, b) => {
        const fa = a.FECHA_PREVISTA ? a.FECHA_PREVISTA.getTime() : 0;
        const fb = b.FECHA_PREVISTA ? b.FECHA_PREVISTA.getTime() : 0;
        return fa - fb;
      });

      /* ========= SET STATE (YA RENDERIZA) ========= */
      setMuestras(filtradas);
      setEstados(estadosData);
      setTallas(tallasData);
      setColores(coloresData);
      setProveedores(proveedoresData);
      setTiposResponsables(responsablesData || []);
      setDesigners([...new Set(designersData.map((d) => d.username))]);
      setMarcas(marcasData);
      setTemporadas([
        ...new Set(filtradas.map((m) => m.TEMPORADA || "NO SEASON")),
      ]);

      /* ========= 2. CARGA SECUNDARIA (NO BLOQUEA) ========= */
      getProducts().then(setProducts).catch(console.error);
      getMuestrasTipo().then(setTiposMuestra).catch(console.error);
      getEscandalloGrupos().then(setEscandalloGrupos).catch(console.error);
      getMovimientosDetallados()
        .then((movimientos) => {
          setMuestras((prev) => applyMovimientosResumen(prev, movimientos));
        })
        .catch(console.error);
      loadNotasCounts();
    } catch (error) {
      console.error("❌ Error al obtener datos:", error);
    }
  };

  //
  // Abrir drawer
  //
  const handleEditMuestra = async (muestra) => {
    try {
      const muestraData = await getMuestraById(muestra.id);
      const escandalloData = await getEscandallosByMuestraId(muestra.id);

      let subgrupos = [];
      if (escandalloData[0]?.ESCANDALLO_GRUPOS) {
        subgrupos = await getEscandalloSubgruposMuestras(
          escandalloData[0].ESCANDALLO_GRUPOS,
        );
      }

      setSelectedMuestra({
        ...muestraData,
        FECHA_PREVISTA: muestraData.FECHA_PREVISTA
          ? new Date(muestraData.FECHA_PREVISTA)
          : null,
        FECHA_GET_SUPPLIER: muestraData.FECHA_GET_SUPPLIER
          ? new Date(muestraData.FECHA_GET_SUPPLIER)
          : null,
        FECHA_PREVISTA_FABRICA: muestraData.FECHA_PREVISTA_FABRICA
          ? new Date(muestraData.FECHA_PREVISTA_FABRICA)
          : null,
        PROVEEDOR_ESCANDALLO: escandalloData[0]?.Proveedor || "",
        ESCANDALLO_GRUPOS: escandalloData[0]?.ESCANDALLO_GRUPOS || "",
        ESCANDALLO_SUBGRUPO: escandalloData[0]?.ESCANDALLO_SUBGRUPO || "",
        UN: escandalloData[0]?.Un || "",
        COSTE: escandalloData[0]?.Coste || "",
        ESCANDALLO_SUBGRUPOS_LIST: subgrupos,
      });

      setOpenDrawer(true);
    } catch (error) {
      console.error("Error al obtener datos de la muestra:", error);
    }
  };

  //
  // Refresh tras guardar/eliminar
  //
  const refreshData = async () => {
    if (!userData) return;

    try {
      const [muestrasData, designersData, temporadasDataRaw, movimientosData] =
        await Promise.all([
          getAllMuestras(),
          getVistaMuestrasDesigners(),
          getMuestrasTemporadas(), // 👈 CLAVE
          getMovimientosDetallados(),
        ]);

      let responsablesData = tiposResponsables;
      try {
        responsablesData = await getMuestraSupplierResponsables();
        if (responsablesData) {
          setTiposResponsables(responsablesData);
        }
      } catch (error) {
        console.error("Error refreshing supplier responsables:", error);
      }

      /* ========= MAPS ========= */
      const estadosMap = estados.reduce((acc, e) => {
        acc[e.ID_ESTADO] = e;
        return acc;
      }, {});

      const tallasMap = tallas.reduce((acc, t) => {
        acc[t.ID_TALLA] = t;
        return acc;
      }, {});

      const coloresMap = colores.reduce((acc, c) => {
        acc[c.ID_COLOR] = c;
        return acc;
      }, {});

      const proveedoresMap = proveedores.reduce((acc, p) => {
        acc[String(p.CODPROV)] = p;
        return acc;
      }, {});

      const designersMap = designersData.reduce((acc, d) => {
        acc[d.ID_MUESTRA] = d;
        return acc;
      }, {});

      const marcasMap = marcas.reduce((acc, m) => {
        acc[String(m.ID_MARCA)] = m;
        return acc;
      }, {});

      const temporadasMap = Object.fromEntries(
        temporadasDataRaw.map((t) => [t.ID_MUESTRA, t]),
      );

      const responsablesMap = Object.fromEntries(
        (responsablesData || []).map((r) => [r.ID, r]),
      );

      /* ========= CONSTRUIR MUESTRAS ========= */
      const updatedMuestras = muestrasData.map((muestra) => {
        const estado = estadosMap[muestra.ID_ESTADO];
        const talla = tallasMap[muestra.ID_TALLA_BASE];
        const color = coloresMap[muestra.ID_COLOR_BASE];
        const proveedor = proveedoresMap[String(muestra.PROVEEDOR)];
        const designer = designersMap[muestra.ID_MUESTRA];
        const marca = marcasMap[String(muestra.ID_Marca)];
        const temporada = temporadasMap[muestra.ID_MUESTRA];
        const responsable =
          muestra.responsableSupplier ||
          responsablesMap[muestra.ID_RESPONSABLE_SUPPLIER];

        return {
          ...muestra,
          ESTADO_NOMBRE: estado?.ESTADO || "Desconocido",
          TALLA_NOMBRE: talla?.TALLA || "Desconocida",
          COLOR_PANTONE: color?.PANTONE || "Desconocido",
          COLOR_HEX: color?.HEX || "Desconocido",
          NOMBRE_PROVEEDOR: proveedor?.N_PROVEEDOR || "Desconocido",
          RESPONSABLE_SUPPLIER: responsable?.Nombre || "",
          DISENADOR: designer?.username || "Desconocido",
          MARCA: marca?.MARCA || "Sin Marca",
          NOMBRE_DISENO: muestra.NombreCliente || "",
          TEMPORADA: temporada?.TEMPORADA || "NO SEASON",
          id: muestra.ID_MUESTRA,
        };
      });

      /* ========= FILTRO CHINA ========= */
      let filtradas = updatedMuestras;

      if (userData?.ProveedoresPermitidos) {
        try {
          const allowed = JSON.parse(userData.ProveedoresPermitidos);
          const list =
            Array.isArray(allowed) && allowed.length > 0 ? allowed : null;
          if (list) {
            filtradas = updatedMuestras.filter((m) =>
              list.includes(m.NOMBRE_PROVEEDOR),
            );
          }
        } catch (_) {
          // ProveedoresPermitidos inválido: no filtrar
        }
      }
      setFilesRefreshKey((prev) => prev + 1);
      setMuestras(applyMovimientosResumen(filtradas, movimientosData));
      await loadNotasCounts();
    } catch (e) {
      console.error("❌ Error al refrescar:", e);
    }
  };

  //
  // Guardar desde drawer
  //
  // La muestra ya está persistida por Drawer.saveMuestra — solo parcheamos la grilla.
  // El refresh completo lo dispara el drawer en segundo plano vía onRefresh.
  const handleSaveMuestra = async (muestraId, updatedMuestra) => {
    try {
      const estadoActualizado = estados.find(
        (e) => e.ID_ESTADO === updatedMuestra.ID_ESTADO,
      );
      const tallaActualizada = tallas.find(
        (t) => t.ID_TALLA === updatedMuestra.ID_TALLA_BASE,
      );
      const colorActualizado = colores.find(
        (c) => c.ID_COLOR === updatedMuestra.ID_COLOR_BASE,
      );
      const proveedorActualizado = proveedores.find(
        (p) => p.CODPROV === updatedMuestra.PROVEEDOR,
      );

      setMuestras((prev) =>
        prev.map((m) =>
          m.id === muestraId
            ? {
                ...m,
                ...updatedMuestra,
                ESTADO_NOMBRE: estadoActualizado?.ESTADO || m.ESTADO_NOMBRE,
                TALLA_NOMBRE: tallaActualizada?.TALLA || m.TALLA_NOMBRE,
                COLOR_PANTONE: colorActualizado?.PANTONE || m.COLOR_PANTONE,
                COLOR_HEX: colorActualizado?.HEX || m.COLOR_HEX,
                NOMBRE_PROVEEDOR:
                  proveedorActualizado?.N_PROVEEDOR || m.NOMBRE_PROVEEDOR,
                RESPONSABLE_SUPPLIER:
                  tiposResponsables.find(
                    (r) => r.ID === updatedMuestra.ID_RESPONSABLE_SUPPLIER,
                  )?.Nombre ||
                  m.RESPONSABLE_SUPPLIER ||
                  "",
                DISENADOR: m.DISENADOR || "Desconocido",
                MARCA:
                  updatedMuestra.MARCA ||
                  updatedMuestra?.marca?.MARCA ||
                  m.MARCA ||
                  "Desconocida",
                NOMBRE_DISENO:
                  updatedMuestra.NombreCliente || m.NOMBRE_DISENO || "",
              }
            : m,
        ),
      );
    } catch (e) {
      console.error("Error actualizando grilla tras guardar:", e);
    }
  };

  //
  // Filtro general
  //
  const normalize = (value) => {
    if (!value) return "";
    return value
      .toString()
      .trim()
      .toLowerCase()
      .normalize("NFD") // 👈 elimina acentos
      .replace(/[\u0300-\u036f]/g, ""); // 👈 elimina diacríticos
  };

  const isMuestraArrived = (row) => {
    if (!row?.FECHA_RECEPCION) return false;
    const d = new Date(row.FECHA_RECEPCION);
    return (
      !Number.isNaN(d.getTime()) &&
      d.getFullYear() > 1900 &&
      d.getTime() <= Date.now()
    );
  };

  const isMuestraSentToClient = (row) =>
    normalize(row?.ESTADO_NOMBRE) === "sent to client";

  const filteredMuestras = React.useMemo(() => {
    const lowerSearch = normalize(searchTerm);

    return muestras.filter((m) => {
      if (!m) return false;

      // 🔎 BUSCADOR GLOBAL ROBUSTO
      const searchableFields = [
        m.MUESTRA,
        m.NOMBRE_DISENO,
        m.MARCA,
        m.NOMBRE_PROVEEDOR,
        m.RESPONSABLE_SUPPLIER,
        m.PRIORIDAD,
        m.DEVELOPMENT,
        ...(Array.isArray(m.COURIERS) ? m.COURIERS : []),
        m.CALIDAD_NOMBRE,
        m.CALIDAD_ESTADO,
        m.PRINT_NOMBRE,
        m.PRINT_ESTADO,
        m.DISENADOR,
        m.TEMPORADA,
        m.ESTADO_NOMBRE,
      ];

      const matchesSearch =
        !lowerSearch ||
        searchableFields.some((field) =>
          normalize(field).includes(lowerSearch),
        );

      // 🎯 FILTROS ESPECÍFICOS
      const matchesDesigner =
        selectedDesigners.length === 0 ||
        selectedDesigners.includes(m.DISENADOR);

      const matchesMarca =
        selectedMarcas.length === 0 || selectedMarcas.includes(m.MARCA);

      const matchesResponsable =
        selectedResponsables.length === 0 ||
        selectedResponsables.includes(m.RESPONSABLE_SUPPLIER);

      const matchesPrioridad =
        selectedPrioridades.length === 0 ||
        selectedPrioridades.includes(m.PRIORIDAD);

      const matchesDevelopment =
        selectedDevelopments.length === 0 ||
        selectedDevelopments.includes(m.DEVELOPMENT);

      const matchesCalidadEstado =
        selectedCalidadEstados.length === 0 ||
        selectedCalidadEstados.includes(m.CALIDAD_ESTADO);

      const matchesPrintEstado =
        selectedPrintEstados.length === 0 ||
        selectedPrintEstados.includes(m.PRINT_ESTADO);

      const matchesEstado =
        selectedEstado.length === 0 || selectedEstado.includes(m.ESTADO_NOMBRE);

      const matchesTalla =
        selectedTallas.length === 0 || selectedTallas.includes(m.TALLA_NOMBRE);

      const matchesColor =
        selectedColores.length === 0 ||
        selectedColores.includes(m.COLOR_PANTONE);

      const matchesProveedor =
        proveedorFilter === "ALL" || m.NOMBRE_PROVEEDOR === proveedorFilter;

      const matchesCliente = !clienteFilter || m.MARCA === clienteFilter;

      const arrived = isMuestraArrived(m);
      const sentToClient = isMuestraSentToClient(m);
      const matchesLifecycleFilter =
        (!hideArrived || !arrived) && (!hideSentToClient || !sentToClient);

      const matchesTemporada =
        selectedTemporadas.length === 0 ||
        selectedTemporadas.includes(m.TEMPORADA);

      const matchesUnreadChat =
        !showUnreadChats ||
        (Number(chatMetaMap[Number(m.ID_MUESTRA || m.id)]?.noLeidos) || 0) > 0;

      return (
        matchesSearch &&
        matchesDesigner &&
        matchesMarca &&
        matchesResponsable &&
        matchesPrioridad &&
        matchesDevelopment &&
        matchesCalidadEstado &&
        matchesPrintEstado &&
        matchesEstado &&
        matchesTalla &&
        matchesColor &&
        matchesProveedor &&
        matchesCliente &&
        matchesLifecycleFilter &&
        matchesTemporada &&
        matchesUnreadChat
      );
    });
  }, [
    muestras,
    searchTerm,
    selectedDesigners,
    selectedMarcas,
    selectedResponsables,
    selectedPrioridades,
    selectedDevelopments,
    selectedCalidadEstados,
    selectedPrintEstados,
    selectedEstado,
    selectedTallas,
    selectedColores,
    proveedorFilter,
    clienteFilter,
    hideArrived,
    hideSentToClient,
    selectedTemporadas,
    showUnreadChats,
    chatMetaMap,
  ]);

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (hideArrived) count += 1;
    if (hideSentToClient) count += 1;
    if (proveedorFilter !== "ALL") count += 1;
    if (selectedDesigners.length > 0) count += 1;
    if (selectedMarcas.length > 0) count += 1;
    if (selectedResponsables.length > 0) count += 1;
    if (selectedPrioridades.length > 0) count += 1;
    if (selectedDevelopments.length > 0) count += 1;
    if (selectedCalidadEstados.length > 0) count += 1;
    if (selectedPrintEstados.length > 0) count += 1;
    if (selectedTemporadas.length > 0) count += 1;
    if (selectedEstado.length > 0) count += 1;
    if (selectedTallas.length > 0) count += 1;
    if (selectedColores.length > 0) count += 1;
    if (showUnreadChats) count += 1;
    return count;
  }, [
    hideArrived,
    hideSentToClient,
    proveedorFilter,
    selectedDesigners,
    selectedMarcas,
    selectedResponsables,
    selectedPrioridades,
    selectedDevelopments,
    selectedCalidadEstados,
    selectedPrintEstados,
    selectedTemporadas,
    selectedEstado,
    selectedTallas,
    selectedColores,
    showUnreadChats,
  ]);

  // Limpiar solo si algo se aparta del default (ocultar ambos ON, resto vacío)
  const canClearFilters =
    selectedMuestraIds.length > 0 ||
    !hideArrived ||
    !hideSentToClient ||
    showUnreadChats ||
    proveedorFilter !== "ALL" ||
    selectedDesigners.length > 0 ||
    selectedMarcas.length > 0 ||
    selectedResponsables.length > 0 ||
    selectedPrioridades.length > 0 ||
    selectedDevelopments.length > 0 ||
    selectedCalidadEstados.length > 0 ||
    selectedPrintEstados.length > 0 ||
    selectedTemporadas.length > 0 ||
    selectedEstado.length > 0 ||
    selectedTallas.length > 0 ||
    selectedColores.length > 0;

  const allFiltersLabel = t("Notes.Todos", { defaultValue: "Todos" });

  const clienteOptions = React.useMemo(() => {
    const names = new Set();
    muestras.forEach((m) => {
      const name = m?.MARCA;
      if (name && name !== "Sin Marca") names.add(name);
    });
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [muestras]);

  const getChatSortRank = (muestra) => {
    const id = Number(muestra?.ID_MUESTRA || muestra?.id);
    const meta = chatMetaMap[id] || {};
    const noLeidos = Number(meta.noLeidos) || 0;
    if (meta.hasMention) return 3_000_000 + noLeidos;
    if (noLeidos > 0) return 2_000_000 + noLeidos;
    const hasMessages =
      Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0;
    if (hasMessages) return 1_000_000;
    return 0;
  };

  const sortedFilteredMuestras = React.useMemo(() => {
    const fechaTs = (m) =>
      m?.FECHA_PREVISTA ? new Date(m.FECHA_PREVISTA).getTime() : 0;

    return [...filteredMuestras].sort((a, b) => {
      const rankDiff = getChatSortRank(b) - getChatSortRank(a);
      if (rankDiff !== 0) return rankDiff;
      return fechaTs(b) - fechaTs(a);
    });
  }, [filteredMuestras, chatMetaMap]);

  const sortedMobileMuestras = React.useMemo(
    () => sortedFilteredMuestras,
    [sortedFilteredMuestras],
  );

  const formatMuestraDate = (value) =>
    value ? new Date(value).toLocaleDateString("es-ES") : "—";

  const handleOpenMuestraInProductTree = (muestra, event) => {
    event?.stopPropagation();
    const productId = muestra?.ID_PRODUCTO;
    const muestraId = muestra?.ID_MUESTRA ?? muestra?.id;
    if (!productId || !muestraId) return;

    navigate(
      `/producttree?productId=${encodeURIComponent(productId)}&muestraId=${encodeURIComponent(muestraId)}`,
    );
  };

  const renderDesignNameLink = (muestra, sx = {}) => {
    const label = muestra.NOMBRE_DISENO || "—";
    if (!muestra.ID_PRODUCTO || label === "—") {
      return (
        <Typography variant="caption" noWrap sx={sx}>
          {label}
        </Typography>
      );
    }

    return (
      <Tooltip title={t("Samples.viewDesignTechFiles")}>
        <Typography
          component="span"
          variant="caption"
          noWrap
          onClick={(e) => handleOpenDesignFiles(muestra, e)}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            ...sx,
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            "&:hover": { opacity: 0.85 },
          }}
        >
          {label}
        </Typography>
      </Tooltip>
    );
  };

  const getEstadoChipSx = (estado) => {
    const key = (estado || "")
      .toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    // Paleta bien diferenciada (no usar primary para todo)
    const colorMap = {
      "in design": "#c2185b",
      ordered: "#f9a825",
      "ok factory": "#2e7d32",
      sent: "#6a1b9a",
      canceled: "#b71c1c",
      cancelled: "#b71c1c",
      arrived: "#00838f",
      revised: "#0277bd",
      "sent to client": "#1565c0",
      approved: "#1b5e20",
      rejected: "#c62828",
      "pdte llegar": "#ef6c00",
    };

    const fromDb = estados.find(
      (e) =>
        (e.ESTADO || "")
          .toString()
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "") === key,
    )?.COLOR;

    const color = colorMap[key] || fromDb || theme.palette.grey[700];

    return {
      height: 22,
      fontWeight: 800,
      fontSize: "0.62rem",
      letterSpacing: 0.2,
      bgcolor: alpha(color, 0.18),
      color,
      border: "1px solid",
      borderColor: alpha(color, 0.55),
      "& .MuiChip-label": {
        px: 0.75,
      },
    };
  };

  const getPrioridadChipSx = (prioridad) => {
    const colorMap = {
      Alta: "#c62828",
      Media: "#ef6c00",
      Normal: "#2e7d32",
    };
    const color = colorMap[prioridad] || theme.palette.grey[700];
    return {
      height: 22,
      fontWeight: 700,
      fontSize: "0.65rem",
      bgcolor: alpha(color, 0.12),
      color,
      border: "1px solid",
      borderColor: alpha(color, 0.28),
    };
  };

  const proveedoresPermitidos = (() => {
    if (!userData?.ProveedoresPermitidos) return [];
    try {
      const parsed = JSON.parse(userData.ProveedoresPermitidos);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const hasProveedorLimit = proveedoresPermitidos.length > 0;

  const proveedorOptions = React.useMemo(
    () =>
      proveedores
        .filter((prov) =>
          proveedoresPermitidos.length === 0
            ? true
            : proveedoresPermitidos.includes(prov.N_PROVEEDOR),
        )
        .map((prov) => prov.N_PROVEEDOR)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [proveedores, proveedoresPermitidos],
  );

  const clearSampleFilters = () => {
    setProveedorFilter("ALL");
    setSelectedDesigners([]);
    setSelectedMarcas([]);
    setSelectedResponsables([]);
    setSelectedPrioridades([]);
    setSelectedDevelopments([]);
    setSelectedCalidadEstados([]);
    setSelectedPrintEstados([]);
    setSelectedTemporadas([]);
    setSelectedEstado([]);
    setSelectedTallas([]);
    setSelectedColores([]);
    setShowUnreadChats(false);
    setHideArrived(true);
    setHideSentToClient(true);
    setSelectedMuestraIds([]);
  };

  const hasFechaEnvio = (row) => {
    if (!row?.FECHA_ENVIO) return false;
    const d = new Date(row.FECHA_ENVIO);
    return !Number.isNaN(d.getTime()) && d.getFullYear() > 1900;
  };

  const hasFechaOkProveedor = (row) => {
    if (!row?.FECHA_GET_SUPPLIER) return false;
    const d = new Date(row.FECHA_GET_SUPPLIER);
    return !Number.isNaN(d.getTime()) && d.getFullYear() > 1900;
  };

  const getEstadoKey = (row) => normalize(row?.ESTADO_NOMBRE);

  const isEstadoOrdered = (row) => {
    const key = getEstadoKey(row);
    return key === "ordered" || Number(row?.ID_ESTADO) === 2;
  };

  const isEstadoOkProveedor = (row) => {
    const key = getEstadoKey(row);
    return (
      key === "ok factory" ||
      key === "ok proveedor" ||
      Number(row?.ID_ESTADO) === 3
    );
  };

  /** Prioridad: enviada (verde) > ok sin envío (naranja) > ordered sin ok (rojo) */
  const getMuestraRowHighlight = (row) => {
    if (hasFechaEnvio(row)) return "enviada";
    if (isEstadoOkProveedor(row)) return "pendiente-envio";
    if (isEstadoOrdered(row) && !hasFechaOkProveedor(row))
      return "pendiente-ok";
    return null;
  };

  const getMuestraRowClassName = (row) => {
    const highlight = getMuestraRowHighlight(row);
    if (highlight === "enviada") return "row-enviada";
    if (highlight === "pendiente-envio") return "row-pendiente-envio";
    if (highlight === "pendiente-ok") return "row-pendiente-ok";
    return "";
  };

  const getMobileHighlightSx = (row) => {
    const highlight = getMuestraRowHighlight(row);
    if (highlight === "enviada") {
      return {
        bgcolor: alpha("#2e7d32", 0.1),
        borderColor: alpha("#2e7d32", 0.35),
        boxShadow: `inset 4px 0 0 ${alpha("#2e7d32", 0.85)}`,
      };
    }
    if (highlight === "pendiente-envio") {
      return {
        bgcolor: alpha("#ef6c00", 0.1),
        borderColor: alpha("#ef6c00", 0.35),
        boxShadow: `inset 4px 0 0 ${alpha("#ef6c00", 0.85)}`,
      };
    }
    if (highlight === "pendiente-ok") {
      return {
        bgcolor: alpha("#c62828", 0.08),
        borderColor: alpha("#c62828", 0.3),
        boxShadow: `inset 4px 0 0 ${alpha("#c62828", 0.8)}`,
      };
    }
    return null;
  };

  const renderRowHighlightLegend = () => (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ rowGap: 0.5 }}
    >
      {[
        {
          color: "#c62828",
          label: t("Samples.legendOrderedNoOk"),
        },
        {
          color: "#ef6c00",
          label: t("Samples.legendOkNoShip"),
        },
        {
          color: "#2e7d32",
          label: t("Samples.legendHasShipDate"),
        },
      ].map((item) => (
        <Stack
          key={item.label}
          direction="row"
          spacing={0.6}
          alignItems="center"
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.5,
              bgcolor: alpha(item.color, 0.35),
              border: `1px solid ${alpha(item.color, 0.7)}`,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const renderMobileMuestraCard = (muestra) => (
    <Box
      key={muestra.id || muestra.ID_MUESTRA}
      onClick={() => handleEditMuestra(muestra)}
      sx={{
        ...pageMobileCardSx(theme, { accentColor: accent }),
        cursor: "pointer",
        ...getMobileHighlightSx(muestra),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Checkbox
          size="small"
          checked={selectedMuestraIds.includes(
            muestra.id || muestra.ID_MUESTRA,
          )}
          onClick={(event) => event.stopPropagation()}
          onChange={(_, checked) => {
            const id = muestra.id || muestra.ID_MUESTRA;
            setSelectedMuestraIds((prev) =>
              checked
                ? prev.includes(id)
                  ? prev
                  : [...prev, id]
                : prev.filter((selectedId) => selectedId !== id),
            );
          }}
          inputProps={{ "aria-label": "Seleccionar muestra para PDF" }}
          sx={{ p: 0.25, mt: 0.25, flexShrink: 0 }}
        />
        <Box
          sx={{
            width: 76,
            minWidth: 76,
            maxHeight: 92,
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.default, 0.6),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& > div": {
              width: "100%",
              justifyContent: "center",
              py: 0,
            },
            "& > div > *:not(:first-child)": {
              display: "none",
            },
            "& .MuiSkeleton-root:not(:first-of-type)": {
              display: "none",
            },
          }}
        >
          <MuestraArchivosCell
            key={`${muestra.id}-${filesRefreshKey}-mobile`}
            muestra={muestra}
            refreshKey={filesRefreshKey}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mb: 0.35 }}
              >
                {!hasProveedorLimit && muestra.DISENADOR ? (
                  <EntityAvatar
                    name={muestra.DISENADOR}
                    color={getDesignerColor(muestra.DISENADOR)}
                    size={28}
                    fontSize="0.65rem"
                    title={muestra.DISENADOR}
                    inline
                  />
                ) : null}
                {muestra.DEVELOPMENT ? (
                  <EntityAvatar
                    name={muestra.DEVELOPMENT}
                    color={getDevelopmentColor(muestra.DEVELOPMENT)}
                    size={28}
                    fontSize="0.58rem"
                    title={
                      muestra.DEVELOPMENT === "Own development"
                        ? t("Samples.developmentOwn")
                        : muestra.DEVELOPMENT === "Client Development"
                          ? t("Samples.developmentClient")
                          : muestra.DEVELOPMENT
                    }
                    inline
                  />
                ) : null}
                <EntityAvatar
                  name={muestra.MARCA}
                  color={getClientColor(muestra.MARCA)}
                  size={28}
                  fontSize="0.65rem"
                  title={muestra.MARCA}
                  inline
                />
                <EntityAvatar
                  name={muestra.NOMBRE_PROVEEDOR}
                  color={getSupplierColor(muestra.NOMBRE_PROVEEDOR)}
                  size={28}
                  fontSize="0.65rem"
                  title={muestra.NOMBRE_PROVEEDOR}
                  inline
                />
              </Stack>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, lineHeight: 1.25 }}
                noWrap
              >
                {muestra.MUESTRA}
              </Typography>
              {muestra.NOMBRE_DISENO
                ? renderDesignNameLink(muestra, {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  })
                : null}
            </Box>
            {muestra.ESTADO_NOMBRE ? (
              <Chip
                size="small"
                label={muestra.ESTADO_NOMBRE}
                sx={{
                  ...getEstadoChipSx(muestra.ESTADO_NOMBRE),
                  flexShrink: 0,
                }}
              />
            ) : null}
            {muestra.MOVIMIENTOS_COUNT > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                label={
                  muestra.ULTIMO_ENVIO_FECHA
                    ? `${muestra.ULTIMO_ENVIO_DESTINO || t("Samples.shipment")} · ${formatMuestraDate(muestra.ULTIMO_ENVIO_FECHA)}`
                    : muestra.ULTIMO_ENVIO_DESTINO || t("Samples.shipment")
                }
                sx={{
                  height: 22,
                  maxWidth: 180,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  flexShrink: 0,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ) : null}
            <Box
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {renderChatDot(muestra)}
              {(() => {
                const { total, openIssueCount, unreadCount } =
                  getMuestraNotasMeta(muestra);
                return (
                  <NotasBadge
                    idMuestra={muestra.ID_MUESTRA || muestra.id}
                    productId={null}
                    notasCount={total}
                    unreadCount={unreadCount}
                    openIssueCount={openIssueCount}
                    onOpenDialog={handleOpenNotas}
                  />
                );
              })()}
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.75 }}
          >
            {muestra.MARCA ? (
              <Chip
                size="small"
                label={muestra.MARCA}
                sx={{ height: 22, fontSize: "0.65rem" }}
              />
            ) : null}
            {muestra.TEMPORADA ? renderTemporadaChip(muestra.TEMPORADA) : null}
            {muestra.TALLA_NOMBRE ? (
              <Chip
                size="small"
                variant="outlined"
                label={muestra.TALLA_NOMBRE}
                sx={{ height: 22, fontSize: "0.65rem" }}
              />
            ) : null}
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 0.75 }}
          >
            {muestra.COLOR_HEX ? (
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: muestra.COLOR_HEX,
                  border: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Typography variant="caption" color="text.secondary" noWrap>
              {muestra.COLOR_PANTONE || "—"}
            </Typography>
          </Stack>

          <Stack spacing={0.35} sx={{ mt: 0.85 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "#90b2b7" }}
            >
              {t("Samples.FECHA_PREVISTA")}:{" "}
              {formatMuestraDate(muestra.FECHA_PREVISTA)}
            </Typography>
            {muestra.NOMBRE_PROVEEDOR ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {muestra.NOMBRE_PROVEEDOR}
              </Typography>
            ) : null}
            {!hasProveedorLimit && muestra.DISENADOR ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {muestra.DISENADOR}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );

  const renderDateCell = (value, color) => {
    if (!value) {
      return (
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      );
    }
    return (
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color,
          whiteSpace: "nowrap",
          px: 0.65,
          py: 0.25,
          borderRadius: 1,
          bgcolor: alpha(color, 0.1),
          border: "1px solid",
          borderColor: alpha(color, 0.18),
        }}
      >
        {new Date(value).toLocaleDateString("es-ES")}
      </Typography>
    );
  };

  const renderTemporadaChip = (temporada) => {
    if (!temporada || temporada === "NO SEASON") {
      return (
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      );
    }

    const color = temporadaColorMap[temporada] || accent;

    return (
      <Chip
        size="small"
        label={temporada}
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 700,
          bgcolor: alpha(color, 0.14),
          color,
          border: `1px solid ${alpha(color, 0.35)}`,
        }}
      />
    );
  };

  const renderUltimoEnvioCell = (row) => {
    const { MOVIMIENTOS_COUNT, ULTIMO_ENVIO_DESTINO, ULTIMO_ENVIO_FECHA } = row;

    if (!MOVIMIENTOS_COUNT) {
      return (
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      );
    }

    const tooltip =
      MOVIMIENTOS_COUNT > 1
        ? t("Samples.shipmentsLastTooltip", {
            count: MOVIMIENTOS_COUNT,
            destination: ULTIMO_ENVIO_DESTINO || "—",
          })
        : ULTIMO_ENVIO_DESTINO || t("Samples.shipmentRegistered");

    return (
      <Tooltip title={tooltip}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            lineHeight: 1.1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.15,
              fontSize: "0.8rem",
              color: theme.palette.primary.dark,
            }}
            noWrap
          >
            {ULTIMO_ENVIO_DESTINO || "—"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ lineHeight: 1.15, fontSize: "0.72rem" }}
          >
            {ULTIMO_ENVIO_FECHA
              ? formatMuestraDate(ULTIMO_ENVIO_FECHA)
              : t("Samples.noDate")}
            {MOVIMIENTOS_COUNT > 1 ? ` · ×${MOVIMIENTOS_COUNT}` : ""}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: "Chat",
      headerName: t("Samples.chat", { defaultValue: "Chat" }),
      width: 48,
      minWidth: 48,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => getChatSortRank(row),
      renderCell: (params) => (
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {renderChatDot(params.row)}
        </Box>
      ),
    },
    {
      field: "Notas",
      headerName: t("Samples.NOTAS"),
      width: 44,
      minWidth: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const muestraId = params.row.ID_MUESTRA || params.row.id;
        const { total, openIssueCount, unreadCount } = getMuestraNotasMeta(
          params.row,
        );
        return (
          <Box
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <NotasBadge
              key={`${muestraId}-${total}-${unreadCount}-${openIssueCount}`}
              idMuestra={muestraId}
              productId={null}
              notasCount={total}
              unreadCount={unreadCount}
              openIssueCount={openIssueCount}
              onOpenDialog={handleOpenNotas}
            />
          </Box>
        );
      },
    },
    // Ocultar columna Designer si el usuario está limitado por proveedor
    ...(!hasProveedorLimit
      ? [
          {
            field: "DISENADOR",
            headerName: t("Samples.Designer"),
            width: 56,
            minWidth: 52,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
              <EntityAvatar
                name={params.value}
                color={getDesignerColor(params.value)}
                size={30}
                fontSize="0.68rem"
                title={params.value}
              />
            ),
          },
        ]
      : []),
    {
      field: "DEVELOPMENT",
      headerName: t("Samples.development"),
      width: 56,
      minWidth: 52,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "—";
        const title =
          value === "Own development"
            ? t("Samples.developmentOwn")
            : value === "Client Development"
              ? t("Samples.developmentClient")
              : value;
        return (
          <EntityAvatar
            name={value}
            color={getDevelopmentColor(value)}
            size={30}
            fontSize="0.62rem"
            title={title}
          />
        );
      },
    },
    {
      field: "MARCA",
      headerName: t("Samples.Client"),
      width: 56,
      minWidth: 52,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <EntityAvatar
          name={params.value}
          color={getClientColor(params.value)}
          size={30}
          fontSize="0.68rem"
          title={params.value}
        />
      ),
    },
    {
      field: "ARCHIVOS",
      headerName: t("Samples.ARCHIVOS"),
      width: 128,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <MuestraArchivosCell
          key={`${params.row.id}-${filesRefreshKey}`}
          muestra={params.row}
        />
      ),
    },
    {
      field: "sample_cliente",
      headerName: t("Samples.Samples"),
      width: 132,
      minWidth: 118,
      maxWidth: 148,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.4,
            minWidth: 0,
            width: "100%",
            lineHeight: 1.05,
          }}
        >
          {params.row.ID_PRODUCTO && canAccessProductTree ? (
            <Tooltip title={t("Samples.openInProductTree")}>
              <IconButton
                size="small"
                aria-label={t("Samples.openInProductTree")}
                onClick={(event) =>
                  handleOpenMuestraInProductTree(params.row, event)
                }
                onMouseDown={(event) => event.stopPropagation()}
                sx={{
                  p: 0.35,
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: "primary.main",
                  },
                }}
              >
                <RemoveRedEyeOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: "0.75rem" }}
              noWrap
            >
              {params.row.MUESTRA}
            </Typography>
            {renderDesignNameLink(params.row, {
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: "0.68rem",
              lineHeight: 1.1,
            })}
          </Box>
        </Box>
      ),
    },
    {
      field: "NOMBRE_PROVEEDOR",
      headerName: t("Samples.Supplier"),
      width: 56,
      minWidth: 52,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <EntityAvatar
          name={params.value}
          color={getSupplierColor(params.value)}
          size={30}
          fontSize="0.68rem"
          title={params.value}
        />
      ),
    },
    {
      field: "RESPONSABLE_SUPPLIER",
      headerName: t("Samples.responsable"),
      width: 110,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value || ""}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "PRIORIDAD",
      headerName: t("Samples.priority"),
      width: 96,
      minWidth: 88,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={
              params.value === "Alta"
                ? t("Samples.priorityHigh")
                : params.value === "Media"
                  ? t("Samples.priorityMedium")
                  : params.value === "Normal"
                    ? t("Samples.priorityNormal")
                    : params.value
            }
            sx={getPrioridadChipSx(params.value)}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "TEMPORADA",
      headerName: t("Samples.season"),
      width: 108,
      minWidth: 92,
      renderCell: (params) => renderTemporadaChip(params.value),
    },
    {
      field: "TALLA_NOMBRE",
      headerName: t("Samples.talla"),
      width: 68,
      minWidth: 56,
    },
    {
      field: "COLOR",
      headerName: t("Samples.color"),
      width: 148,
      minWidth: 120,
      renderCell: (params) => {
        const label = params.row.COLOR_PANTONE || "";

        return (
          <Tooltip title={`${params.row.COLOR_HEX}`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: params.row.COLOR_HEX,
                  borderRadius: "50%",
                  border: `1px solid ${theme.palette.divider}`,
                  flexShrink: 0, // 👈 evita que el círculo se reduzca
                }}
              />

              <Box
                component="span"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  display: "block",
                }}
              >
                {label}
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },

    {
      field: "FECHA_PREVISTA",
      headerName: t("Samples.FECHA_PREVISTA"),
      width: 132,
      minWidth: 120,
      renderCell: (params) => renderDateCell(params.value, "#5c7a8a"),
    },
    {
      field: "FECHA_PREVISTA_FABRICA",
      headerName: t("Samples.FECHA_PREVISTA_FABRICA"),
      width: 148,
      minWidth: 132,
      renderCell: (params) => renderDateCell(params.value, "#455a64"),
    },
    {
      field: "FECHA_PETICION",
      headerName: t("Samples.FECHA_PETICION"),
      width: 108,
      renderCell: (params) => renderDateCell(params.value, "#7e57c2"),
    },
    {
      field: "FECHA_GET_SUPPLIER",
      headerName: t("Samples.FECHA_GET_SUPPLIER"),
      width: 112,
      renderCell: (params) =>
        renderDateCell(params.value, theme.palette.warning.dark),
    },
    {
      field: "FECHA_ENVIO",
      headerName: t("Samples.FECHA_ENVIO"),
      width: 104,
      renderCell: (params) =>
        renderDateCell(params.value, theme.palette.error.main),
    },
    {
      field: "ESTADO_NOMBRE",
      headerName: t("Samples.Status"),
      width: 118,
      minWidth: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip
            size="small"
            label={params.value}
            sx={getEstadoChipSx(params.value)}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "ULTIMO_ENVIO",
      headerName: t("Samples.lastShipment"),
      width: 132,
      minWidth: 112,
      sortable: false,
      valueGetter: (_value, row) =>
        row.ULTIMO_ENVIO_FECHA ? new Date(row.ULTIMO_ENVIO_FECHA).getTime() : 0,
      renderCell: (params) => renderUltimoEnvioCell(params.row),
    },
    {
      field: "COURIERS",
      headerName: t("Samples.courierNumber"),
      width: 120,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const list = Array.isArray(params.value)
          ? params.value.filter(Boolean)
          : [];
        if (!list.length) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              py: 0.25,
              lineHeight: 1.25,
              overflow: "hidden",
            }}
          >
            {list.map((c, i) => (
              <Typography
                key={`${c}-${i}`}
                variant="body2"
                noWrap
                title={c}
                sx={{ fontSize: "0.72rem" }}
              >
                {c}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    {
      field: "FECHA_RECEPCION",
      headerName: t("Samples.FECHA_RECEPCION"),
      width: 112,
      renderCell: (params) =>
        renderDateCell(params.value, theme.palette.success.dark),
    },
    {
      field: "CALIDAD_NOMBRE",
      headerName: t("Samples.calidades"),
      width: 220,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => (
        <MuestraCategoryFilesCell
          key={`${params.row.id}-calidades-${filesRefreshKey}`}
          muestra={params.row}
          category="Calidades"
          nombre={params.row.CALIDAD_NOMBRE}
          estado={params.row.CALIDAD_ESTADO}
          refreshKey={filesRefreshKey}
        />
      ),
    },
    {
      field: "PRINT_NOMBRE",
      headerName: t("Samples.print"),
      width: 220,
      minWidth: 190,
      sortable: false,
      renderCell: (params) => (
        <MuestraCategoryFilesCell
          key={`${params.row.id}-print-${filesRefreshKey}`}
          muestra={params.row}
          category="Print"
          nombre={params.row.PRINT_NOMBRE}
          estado={params.row.PRINT_ESTADO}
          refreshKey={filesRefreshKey}
        />
      ),
    },
  ];

  const sectionPxSx = { px: { xs: 1.5, md: 3 } };

  const filterFieldSx = {
    ...pageFieldSx(theme),
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      backgroundColor: alpha(theme.palette.background.paper, 0.98),
      borderRadius: 1.25,
      fontSize: "0.8125rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.72),
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  };

  const inlineSearchSx = {
    ...filterFieldSx,
    width: { xs: "100%", sm: 180, md: 200 },
    flexShrink: 1,
    minWidth: { md: 140 },
    "& .MuiInputBase-root": {
      ...filterFieldSx["& .MuiInputBase-root"],
      height: 36,
    },
  };

  const toolbarSelectSx = {
    ...filterFieldSx,
    minWidth: { xs: "100%", md: 160 },
    maxWidth: { xs: "100%", md: 200 },
    flexShrink: 1,
    "& .MuiInputBase-root": {
      ...filterFieldSx["& .MuiInputBase-root"],
      height: 36,
    },
  };

  const selectMenuProps = {
    disableScrollLock: true,
    PaperProps: {
      elevation: 8,
      sx: {
        mt: 0.5,
        borderRadius: 2,
        maxHeight: 320,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        "& .MuiMenuItem-root": {
          fontSize: "0.8125rem",
          py: 0.875,
          borderRadius: 1,
          mx: 0.5,
          my: 0.25,
          "&.Mui-selected": {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            fontWeight: 700,
          },
          "&.Mui-selected:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.18),
          },
        },
      },
    },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  const filterPopoverFieldSx = {
    ...filterFieldSx,
    width: "100%",
    "& .MuiInputBase-root": {
      ...filterFieldSx["& .MuiInputBase-root"],
      height: 36,
      fontSize: "0.8125rem",
      borderRadius: 1.25,
      bgcolor: theme.palette.background.paper,
    },
  };

  const filterSelectMenuProps = selectMenuProps;

  const renderFilterField = (label, control) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          fontSize: "0.7rem",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      {control}
    </Box>
  );

  const renderMultiSelect = (value, onChange, options, active) => (
    <FormControl size="small" sx={filterPopoverFieldSx}>
      <Select
        multiple
        value={value}
        onChange={onChange}
        displayEmpty
        MenuProps={filterSelectMenuProps}
        renderValue={(selected) =>
          selected.length ? selected.join(", ") : t("Samples.all")
        }
        sx={{
          fontWeight: active ? 700 : 500,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: active
              ? alpha(theme.palette.primary.main, 0.45)
              : alpha(theme.palette.divider, 0.7),
          },
        }}
      >
        {options}
      </Select>
    </FormControl>
  );

  const renderSingleSelect = (value, onChange, children, active) => {
    const display = !value || value === "ALL" ? allFiltersLabel : String(value);
    return (
      <FormControl size="small" sx={filterPopoverFieldSx}>
        <Select
          value={value}
          onChange={(e) => {
            onChange(e);
            requestAnimationFrame(() => e.target?.blur?.());
          }}
          displayEmpty
          MenuProps={filterSelectMenuProps}
          renderValue={() => (
            <Typography
              component="span"
              noWrap
              title={display}
              sx={{
                display: "block",
                fontSize: "0.8125rem",
                fontWeight: active ? 700 : 500,
              }}
            >
              {display}
            </Typography>
          )}
          sx={{
            fontWeight: active ? 700 : 500,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: active
                ? alpha(theme.palette.primary.main, 0.45)
                : alpha(theme.palette.divider, 0.7),
            },
          }}
        >
          {children}
        </Select>
      </FormControl>
    );
  };

  const desktopGridSx = {
    flex: 1,
    minHeight: 0,
    height: "100%",
    width: "100%",
    border: "none",
    borderRadius: 0,
    bgcolor: "transparent",
    ...pageDataGridSx(theme),
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
    },
    "& .MuiDataGrid-columnHeadersInner": {
      width: "100%",
    },
    "& .MuiDataGrid-columnHeader": {
      fontWeight: 800,
      px: 1.25,
      display: "flex",
      alignItems: "center",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 800,
      fontSize: "0.68rem",
      letterSpacing: 0.45,
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": {
        bgcolor: `${alpha(theme.palette.primary.main, 0.07)} !important`,
      },
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
      bgcolor: alpha(theme.palette.background.default, 0.4),
    },
    "& .MuiDataGrid-row.row-enviada": {
      bgcolor: `${alpha("#2e7d32", 0.1)} !important`,
      boxShadow: `inset 4px 0 0 ${alpha("#2e7d32", 0.85)}`,
      "&:hover": {
        bgcolor: `${alpha("#2e7d32", 0.16)} !important`,
      },
    },
    "& .MuiDataGrid-row.row-enviada:nth-of-type(even)": {
      bgcolor: `${alpha("#2e7d32", 0.14)} !important`,
    },
    "& .MuiDataGrid-row.row-pendiente-envio": {
      bgcolor: `${alpha("#ef6c00", 0.1)} !important`,
      boxShadow: `inset 4px 0 0 ${alpha("#ef6c00", 0.85)}`,
      "&:hover": {
        bgcolor: `${alpha("#ef6c00", 0.16)} !important`,
      },
    },
    "& .MuiDataGrid-row.row-pendiente-envio:nth-of-type(even)": {
      bgcolor: `${alpha("#ef6c00", 0.14)} !important`,
    },
    "& .MuiDataGrid-row.row-pendiente-ok": {
      bgcolor: `${alpha("#c62828", 0.08)} !important`,
      boxShadow: `inset 4px 0 0 ${alpha("#c62828", 0.8)}`,
      "&:hover": {
        bgcolor: `${alpha("#c62828", 0.14)} !important`,
      },
    },
    "& .MuiDataGrid-row.row-pendiente-ok:nth-of-type(even)": {
      bgcolor: `${alpha("#c62828", 0.11)} !important`,
    },
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      fontSize: "0.84rem",
      lineHeight: 1.35,
      py: 0,
      borderBottom: "none",
    },
    "& .MuiDataGrid-cell[data-field='sample_cliente']": {
      alignItems: "center",
      py: 0,
    },
    "& .MuiDataGrid-cell[data-field='ULTIMO_ENVIO']": {
      alignItems: "center",
      py: 0,
    },
    "& .MuiDataGrid-cellContent": {
      display: "flex",
      alignItems: "center",
      width: "100%",
      minWidth: 0,
      height: "100%",
      overflow: "hidden",
      lineHeight: 1.35,
    },
    "& .MuiDataGrid-cell[data-field='sample_cliente'] .MuiDataGrid-cellContent":
      {
        alignItems: "center",
        height: "100%",
      },
    "& .MuiDataGrid-cell[data-field='ULTIMO_ENVIO'] .MuiDataGrid-cellContent": {
      alignItems: "center",
      height: "100%",
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
      bgcolor: alpha(theme.palette.background.default, 0.35),
      minHeight: 48,
    },
  };

  const pageShellSx = {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    minHeight: "100dvh",
    maxHeight: "100dvh",
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.default,
    ...contentMargin,
  };

  const pageHeaderSx = {
    ...sectionPxSx,
    pt: { xs: 1, md: 1.25 },
    pb: { xs: 0.75, md: 0.75 },
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.background.default, 0.96),
    backdropFilter: "blur(8px)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: 9,
  };

  const pageScrollSx = {
    ...sectionPxSx,
    pt: 1,
    pb: { xs: 10, md: 2 },
    pr: { xs: 0.75, md: 1.25 },
    flex: 1,
    minHeight: 0,
    overflowY: { xs: "auto", md: "hidden" },
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Box sx={pageShellSx}>
      <Box sx={pageHeaderSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1, md: 1.5 }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0, flex: "0 1 auto" }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            >
              <DryCleaningOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  fontSize: { xs: "1.05rem", md: "1.12rem" },
                }}
              >
                {t("Samples.Samples")}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 0.35, rowGap: 0.35 }}
              >
                <Chip
                  label={t("Samples.visibleCount", {
                    count: sortedFilteredMuestras.length,
                  })}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
                />
                <Chip
                  label={t("Samples.totalCount", {
                    count: muestras.length,
                  })}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: alpha(accent, 0.1),
                    color: theme.palette.primary.dark,
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            flexWrap={{ xs: "wrap", md: "nowrap" }}
            sx={{
              flex: "1 1 auto",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              rowGap: 0.5,
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
            }}
          >
            <Select
              size="small"
              displayEmpty
              value={clienteFilter}
              onChange={(e) => setClienteFilter(e.target.value)}
              MenuProps={selectMenuProps}
              sx={toolbarSelectSx}
              renderValue={(selected) => selected || t("Samples.selectClient")}
            >
              <MenuItem value="">
                <em>{t("Samples.selectClient")}</em>
              </MenuItem>
              {clienteOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              placeholder={t("Samples.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={inlineSearchSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Badge
              color="primary"
              badgeContent={activeFilterCount}
              invisible={activeFilterCount === 0}
              overlap="rectangular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.65rem",
                  minWidth: 18,
                  height: 18,
                  top: 4,
                  right: 4,
                },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<FilterListOutlinedIcon />}
                onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
                sx={{
                  height: 36,
                  px: 1.25,
                  fontWeight: 700,
                  borderRadius: 1.25,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  borderColor:
                    activeFilterCount > 0
                      ? alpha(theme.palette.primary.main, 0.55)
                      : alpha(theme.palette.divider, 0.85),
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                }}
              >
                {t("Samples.filtersLabel")}
              </Button>
            </Badge>

            <MuestrasExportExcelDialog
              muestras={sortedFilteredMuestras}
              selectedMuestras={muestras.filter((muestra) =>
                selectedMuestraIds.includes(muestra.id || muestra.ID_MUESTRA),
              )}
              selectedIds={selectedMuestraIds}
              buttonProps={{
                sx: {
                  height: 36,
                  fontWeight: 700,
                  borderRadius: 1.25,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                },
              }}
            />
            <MuestrasExportPdfDialog
              muestras={muestras}
              selectedIds={selectedMuestraIds}
              buttonProps={{
                size: "small",
                sx: {
                  height: 36,
                  fontWeight: 700,
                  borderRadius: 1.25,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                },
              }}
            />
            <Tooltip title={t("Samples.cameraPresentationTooltip")}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraOutlinedIcon />}
                onClick={() => setCameraPresentationOpen(true)}
                sx={{
                  height: 36,
                  px: 1.25,
                  fontWeight: 700,
                  borderRadius: 1.25,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  borderColor: alpha(theme.palette.divider, 0.85),
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                }}
              >
                {t("Samples.cameraPresentationButton")}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={cameraPresentationOpen}
        onClose={() => setCameraPresentationOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          {t("Samples.cameraPresentationTitle")}
          <IconButton
            aria-label={t("common.close")}
            onClick={() => setCameraPresentationOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {cameraPresentationOpen ? (
            <MuestrasPresentacionCamara muestras={muestras} embedded />
          ) : null}
        </DialogContent>
      </Dialog>

      <Popover
        open={Boolean(filtersAnchorEl)}
        anchorEl={filtersAnchorEl}
        onClose={() => setFiltersAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "min(100vw - 24px, 360px)", sm: 360 },
              height: { xs: "min(88vh, 760px)", sm: "min(88vh, 780px)" },
              maxHeight: "88vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              boxShadow: `0 10px 36px ${alpha(theme.palette.common.black, 0.12)}`,
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
            {t("Samples.filtersLabel")}
          </Typography>
          {canClearFilters ? (
            <Button
              size="small"
              onClick={clearSampleFilters}
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                minWidth: 0,
                px: 0.75,
              }}
            >
              {t("Samples.clearFilters", { defaultValue: "Limpiar" })}
            </Button>
          ) : null}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.75,
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack spacing={0.15} sx={{ mb: 1.75 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={hideArrived}
                  onChange={(e) => setHideArrived(e.target.checked)}
                />
              }
              label={t("Samples.hideArrived", {
                defaultValue: "Ocultar Llegadas",
              })}
              sx={{
                m: 0,
                ml: -0.5,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={hideSentToClient}
                  onChange={(e) => setHideSentToClient(e.target.checked)}
                />
              }
              label={t("Samples.hideSentToClient", {
                defaultValue: "Ocultar Sent to Client",
              })}
              sx={{
                m: 0,
                ml: -0.5,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showUnreadChats}
                  onChange={(e) => setShowUnreadChats(e.target.checked)}
                />
              }
              label={t("Samples.showUnreadChats", {
                defaultValue: "Enseñar chats sin leer",
              })}
              sx={{
                m: 0,
                ml: -0.5,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                },
              }}
            />
          </Stack>

          <Divider sx={{ mb: 1.75 }} />

          <Stack spacing={1.35}>
            {!hasProveedorLimit
              ? renderFilterField(
                  t("NotasResumenPage.proveedor", {
                    defaultValue: "Proveedor",
                  }),
                  renderSingleSelect(
                    proveedorFilter,
                    (e) => setProveedorFilter(e.target.value),
                    [
                      <MenuItem key="ALL" value="ALL">
                        {allFiltersLabel}
                      </MenuItem>,
                      ...proveedorOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      )),
                    ],
                    proveedorFilter !== "ALL",
                  ),
                )
              : null}

            {!hasProveedorLimit
              ? renderFilterField(
                  t("Samples.Designer"),
                  renderMultiSelect(
                    selectedDesigners,
                    (e) => setSelectedDesigners(e.target.value),
                    designers.map((designer) => (
                      <MenuItem key={designer} value={designer}>
                        <Checkbox
                          size="small"
                          checked={selectedDesigners.includes(designer)}
                        />
                        <ListItemText primary={designer} />
                      </MenuItem>
                    )),
                    selectedDesigners.length > 0,
                  ),
                )
              : null}

            {!hasProveedorLimit
              ? renderFilterField(
                  t("Samples.brand"),
                  renderMultiSelect(
                    selectedMarcas,
                    (e) => setSelectedMarcas(e.target.value),
                    marcas.map((marca) => (
                      <MenuItem key={marca.MARCA} value={marca.MARCA}>
                        <Checkbox
                          size="small"
                          checked={selectedMarcas.includes(marca.MARCA)}
                        />
                        <ListItemText primary={marca.MARCA} />
                      </MenuItem>
                    )),
                    selectedMarcas.length > 0,
                  ),
                )
              : null}

            {renderFilterField(
              t("Samples.responsableInSupplier"),
              renderMultiSelect(
                selectedResponsables,
                (e) => setSelectedResponsables(e.target.value),
                tiposResponsables.map((responsable) => (
                  <MenuItem key={responsable.ID} value={responsable.Nombre}>
                    <Checkbox
                      size="small"
                      checked={selectedResponsables.includes(
                        responsable.Nombre,
                      )}
                    />
                    <ListItemText primary={responsable.Nombre} />
                  </MenuItem>
                )),
                selectedResponsables.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.priority"),
              renderMultiSelect(
                selectedPrioridades,
                (e) => setSelectedPrioridades(e.target.value),
                [
                  { value: "Alta", label: t("Samples.priorityHigh") },
                  { value: "Media", label: t("Samples.priorityMedium") },
                  { value: "Normal", label: t("Samples.priorityNormal") },
                ].map((prioridad) => (
                  <MenuItem key={prioridad.value} value={prioridad.value}>
                    <Checkbox
                      size="small"
                      checked={selectedPrioridades.includes(prioridad.value)}
                    />
                    <ListItemText primary={prioridad.label} />
                  </MenuItem>
                )),
                selectedPrioridades.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.development"),
              renderMultiSelect(
                selectedDevelopments,
                (e) => setSelectedDevelopments(e.target.value),
                [
                  {
                    value: "Own development",
                    label: t("Samples.developmentOwn"),
                  },
                  {
                    value: "Client Development",
                    label: t("Samples.developmentClient"),
                  },
                ].map((development) => (
                  <MenuItem key={development.value} value={development.value}>
                    <Checkbox
                      size="small"
                      checked={selectedDevelopments.includes(development.value)}
                    />
                    <ListItemText primary={development.label} />
                  </MenuItem>
                )),
                selectedDevelopments.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.calidadesStatus"),
              renderMultiSelect(
                selectedCalidadEstados,
                (e) => setSelectedCalidadEstados(e.target.value),
                ["Pedido", "Pendiente", "Recibido"].map((estado) => (
                  <MenuItem key={estado} value={estado}>
                    <Checkbox
                      size="small"
                      checked={selectedCalidadEstados.includes(estado)}
                    />
                    <ListItemText primary={estado} />
                  </MenuItem>
                )),
                selectedCalidadEstados.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.printStatus"),
              renderMultiSelect(
                selectedPrintEstados,
                (e) => setSelectedPrintEstados(e.target.value),
                ["Pedido", "Pendiente", "Recibido"].map((estado) => (
                  <MenuItem key={estado} value={estado}>
                    <Checkbox
                      size="small"
                      checked={selectedPrintEstados.includes(estado)}
                    />
                    <ListItemText primary={estado} />
                  </MenuItem>
                )),
                selectedPrintEstados.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.season"),
              renderMultiSelect(
                selectedTemporadas,
                (e) => setSelectedTemporadas(e.target.value),
                temporadas.map((temp) => (
                  <MenuItem key={temp} value={temp}>
                    <Checkbox
                      size="small"
                      checked={selectedTemporadas.includes(temp)}
                    />
                    <ListItemText primary={temp} />
                  </MenuItem>
                )),
                selectedTemporadas.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.Status"),
              renderMultiSelect(
                selectedEstado,
                (e) => setSelectedEstado(e.target.value),
                estados.map((estado) => (
                  <MenuItem key={estado.ESTADO} value={estado.ESTADO}>
                    <Checkbox
                      size="small"
                      checked={selectedEstado.includes(estado.ESTADO)}
                    />
                    <ListItemText primary={estado.ESTADO} />
                  </MenuItem>
                )),
                selectedEstado.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.talla"),
              renderMultiSelect(
                selectedTallas,
                (e) => setSelectedTallas(e.target.value),
                tallas.map((talla) => (
                  <MenuItem key={talla.TALLA} value={talla.TALLA}>
                    <Checkbox
                      size="small"
                      checked={selectedTallas.includes(talla.TALLA)}
                    />
                    <ListItemText primary={talla.TALLA} />
                  </MenuItem>
                )),
                selectedTallas.length > 0,
              ),
            )}

            {renderFilterField(
              t("Samples.color"),
              renderMultiSelect(
                selectedColores,
                (e) => setSelectedColores(e.target.value),
                colores.map((color) => (
                  <MenuItem key={color.PANTONE} value={color.PANTONE}>
                    <Checkbox
                      size="small"
                      checked={selectedColores.includes(color.PANTONE)}
                    />
                    <ListItemText primary={color.PANTONE} />
                  </MenuItem>
                )),
                selectedColores.length > 0,
              ),
            )}
          </Stack>
        </Box>
      </Popover>

      <Box sx={pageScrollSx}>
        {isMobile ? (
          <Stack spacing={1.25}>
            <Box
              sx={{
                px: 0.25,
                pb: 0.25,
              }}
            >
              {renderRowHighlightLegend()}
            </Box>
            {sortedMobileMuestras.length === 0 ? (
              <Box
                sx={{
                  py: 5,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                }}
              >
                <Typography color="text.secondary" fontWeight={600}>
                  No hay muestras con los filtros actuales
                </Typography>
              </Box>
            ) : (
              sortedMobileMuestras.map((muestra) =>
                renderMobileMuestraCard(muestra),
              )
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              ...pageContentCardSx(theme, accent),
              p: 0,
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
                borderBottom: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.75),
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: "1 1 220px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {t("Samples.sampleCount", {
                    count: sortedFilteredMuestras.length,
                  })}
                </Typography>
                {renderRowHighlightLegend()}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {t("Samples.clickRowToOpen")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
              <DataGrid
                rows={sortedFilteredMuestras}
                columns={columns}
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50, 100]}
                rowHeight={52}
                columnHeaderHeight={48}
                disableSelectionOnClick
                disableRowSelectionOnClick
                disableColumnMenu
                checkboxSelection
                keepNonExistentRowsSelected
                rowSelectionModel={selectedMuestraIds}
                onRowSelectionModelChange={(model) =>
                  setSelectedMuestraIds(Array.from(model))
                }
                sx={desktopGridSx}
                getRowId={(row) => row.id || row.ID_MUESTRA}
                getRowClassName={(params) => getMuestraRowClassName(params.row)}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "Chat", sort: "desc" }],
                  },
                }}
                onRowClick={(params) => handleEditMuestra(params.row)}
              />
            </Box>
          </Box>
        )}
      </Box>

      {selectedMuestra && (
        <DrawerMuestraV2
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          onSaveMuestra={handleSaveMuestra} // 👈 CLAVE
          onRefresh={refreshData} // opcional
          newMuestra={selectedMuestra}
          setNewMuestra={setSelectedMuestra}
          handleSaveMuestra={(muestraId, updatedMuestra) =>
            handleSaveMuestra(muestraId, updatedMuestra)
          }
          tiposMuestra={tiposMuestra}
          tiposEstado={estados}
          tiposTallas={tallas}
          tiposColores={colores}
          tiposMarcas={marcas} // Aquí pasamos las marcas
          tiposProveedores={proveedores}
          escandalloGrupos={escandalloGrupos}
          escandalloSubgrupos={selectedMuestra?.ESCANDALLO_SUBGRUPOS_LIST || []}
          handleGrupoChange={() => {}}
          isFromTree={false}
          isEditMode={true}
          isFromMuestras={true}
          userHasProveedorLimit={hasProveedorLimit} /* CHINA */
          userCanCreateSample={userCanCreateSample}
          /** 👇👇 NUEVO — elimina visualmente del DataGrid */
          onDeleteMuestra={(id) => {
            setMuestras((prev) => prev.filter((m) => m.id !== id));
          }}
          selectedProduct={{
            productName: selectedMuestra?.producto?.nombre, // 👈 correcto
            temporada: selectedMuestra?.TEMPORADA,
            marca: selectedMuestra?.MARCA,
            designer: selectedMuestra?.DISENADOR,
          }}
        />
      )}

      <DesignTechnicalFilesDialog
        open={designFilesDialog.open}
        onClose={handleCloseDesignFiles}
        productId={designFilesDialog.productId}
        muestraId={designFilesDialog.muestraId}
        muestraName={designFilesDialog.muestraName}
        clientName={designFilesDialog.clientName}
        designLabel={designFilesDialog.designLabel}
      />

      <MuestraChat
        open={chatOpen}
        muestra={chatMuestra}
        initialTab={chatInitialTab}
        onNotasChanged={({ changed, idMuestra }) => {
          if (changed && idMuestra != null) {
            refreshMuestraNotasMeta(idMuestra);
          }
        }}
        onClose={() => {
          setChatOpen(false);
          setChatMuestra(null);
          setChatInitialTab("mensajes");
          loadChatSummary();
          loadNotasCounts();
        }}
      />
    </Box>
  );
};

export default Muestras;
